import { useEffect, useRef, useState } from 'react';
import { ConfiguredLoadingVisual } from '@/components/ui/configured-loading-visual';
import { useLoadingCoordinator } from '@/hooks/useLoadingCoordinator';
import {
  getBootHandoffRemainingMs,
  getCoordinatorVisibleState,
} from '@/lib/loading/loadingCoordinatorStore';
import {
  GLOBAL_OVERLAY_MIN_VISIBLE_MS,
  GLOBAL_OVERLAY_SHOW_DELAY_MS,
} from '@/lib/overlayTimingConstants';
import { cn } from '@/lib/utils';

const fullscreenShellClass =
  'fixed inset-0 z-[100] flex items-center justify-center bg-background page-loader-bg';

/**
 * Single runtime loading surface — only component that renders coordinator-owned visuals.
 */
function coordinatorDomMarkers(state: ReturnType<typeof getCoordinatorVisibleState>) {
  return {
    'data-loading-coordinator-surface': state.surface,
    'data-loading-surface': state.surface,
    'data-loading-source': state.activeRequest?.source ?? 'none',
    'data-loading-owner': state.owner,
    'data-loading-phase': state.phase,
    'data-loading-transition-id': state.transitionId,
  } as const;
}

export function LoadingSurface() {
  const coordinator = useLoadingCoordinator();
  const { surface } = coordinator;
  const [visible, setVisible] = useState(false);
  const showTimeRef = useRef(0);
  const showDelayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hideDelayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const wantsFullscreen = surface === 'fullscreen';
  const wantsRoute = surface === 'route';
  const wantsContent = surface === 'content';
  const wantsVisual = wantsFullscreen || wantsRoute || wantsContent;

  useEffect(() => {
    const clearShowTimer = () => {
      if (showDelayTimerRef.current) {
        window.clearTimeout(showDelayTimerRef.current);
        showDelayTimerRef.current = null;
      }
    };

    const scheduleShow = () => {
      clearShowTimer();
      const delay = wantsFullscreen ? GLOBAL_OVERLAY_SHOW_DELAY_MS : 0;
      showDelayTimerRef.current = window.setTimeout(() => {
        showDelayTimerRef.current = null;
        const state = getCoordinatorVisibleState();
        if (state.surface !== surface) return;
        showTimeRef.current = Date.now();
        setVisible(true);
      }, delay);
    };

    const scheduleShowRespectingHandoff = () => {
      const handoffRemaining = getBootHandoffRemainingMs();
      if (handoffRemaining > 0 && wantsFullscreen) {
        clearShowTimer();
        showDelayTimerRef.current = window.setTimeout(() => {
          showDelayTimerRef.current = null;
          if (getCoordinatorVisibleState().surface === 'fullscreen') scheduleShow();
        }, handoffRemaining);
        return;
      }
      scheduleShow();
    };

    if (wantsVisual) {
      scheduleShowRespectingHandoff();
      if (hideDelayTimerRef.current) {
        window.clearTimeout(hideDelayTimerRef.current);
        hideDelayTimerRef.current = null;
      }
      return clearShowTimer;
    }

    clearShowTimer();

    if (!visible) return;

    const elapsed = Date.now() - showTimeRef.current;
    const minVisible = wantsFullscreen ? GLOBAL_OVERLAY_MIN_VISIBLE_MS : 0;
    const delay = Math.max(0, minVisible - elapsed);
    hideDelayTimerRef.current = window.setTimeout(() => {
      hideDelayTimerRef.current = null;
      setVisible(false);
    }, delay);

    return () => {
      if (hideDelayTimerRef.current) {
        window.clearTimeout(hideDelayTimerRef.current);
        hideDelayTimerRef.current = null;
      }
    };
  }, [wantsVisual, wantsFullscreen, surface, visible]);

  useEffect(() => {
    return () => {
      if (showDelayTimerRef.current) window.clearTimeout(showDelayTimerRef.current);
      if (hideDelayTimerRef.current) window.clearTimeout(hideDelayTimerRef.current);
    };
  }, []);

  /** Content/route surfaces show immediately — no show-delay blank gap. Fullscreen keeps handoff timing. */
  const effectiveVisible = wantsFullscreen ? visible : wantsVisual;
  if (!effectiveVisible || !wantsVisual) return null;

  const markers = coordinatorDomMarkers(coordinator);

  if (wantsFullscreen) {
    return (
      <div
        className={fullscreenShellClass}
        data-page-loader="true"
        {...markers}
        role="status"
        aria-live="polite"
      >
        <ConfiguredLoadingVisual scope="fullscreen" />
      </div>
    );
  }

  if (wantsRoute) {
    return (
      <div
        className="pointer-events-none fixed inset-x-0 top-16 z-40 flex justify-center lg:top-20"
        data-route-chunk-loader="true"
        {...markers}
        aria-hidden="true"
      >
        <ConfiguredLoadingVisual scope="content" className="mx-auto" presentation="runtime" />
      </div>
    );
  }

  return (
    <div
      className={cn(
        'pointer-events-none fixed inset-0 z-30 flex items-center justify-center',
        'lg:pl-[var(--admin-sidebar-width,280px)]',
      )}
      {...markers}
      aria-hidden="true"
    >
      <ConfiguredLoadingVisual scope="content" className="mx-auto" presentation="runtime" />
    </div>
  );
}
